import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';

export default function Register() {
  const { isDark } = useTheme();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Şifreler eşleşmiyor.');
      return;
    }

    if (password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.');
      return;
    }

    setIsLoading(true);

    const { error: registerError } = await register(email, password, fullName, phone);

    setIsLoading(false);

    if (registerError) {
      setError(registerError);
      return;
    }

    setSuccess(true);
    setTimeout(() => {
      navigate('/login');
    }, 2500);
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img
            src="https://public.readdy.ai/ai/img_res/8d501578-6f11-4960-92e0-74720553c4c2.png"
            alt="EmlakPro Logo"
            className="w-20 h-20 object-contain mb-4"
          />
          <h1 className={`text-2xl font-bold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
            EmlakPro
          </h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
            Emlak Danışmanı Paneli
          </p>
        </div>

        {/* Register Card */}
        <div
          className={`rounded-2xl p-6 md:p-8 shadow-soft ${
            isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'
          }`}
        >
          <h2 className={`text-lg font-semibold mb-6 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
            Yeni Hesap Oluştur
          </h2>

          {success && (
            <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-green-600 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-checkbox-circle-line" />
                </div>
                Hesabınız oluşturuldu! Giriş sayfasına yönlendiriliyorsunuz...
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full Name */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                Ad Soyad
              </label>
              <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors ${
                isDark
                  ? 'bg-dark-bg border-dark-border focus-within:border-primary-500'
                  : 'bg-light-bg border-light-border focus-within:border-primary-500'
              }`}>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-user-line ${isDark ? 'text-dark-muted' : 'text-light-muted'}`} />
                </div>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Ahmet Yılmaz"
                  className={`flex-1 bg-transparent text-sm outline-none ${
                    isDark ? 'text-dark-text placeholder:text-dark-muted' : 'text-light-text placeholder:text-light-muted'
                  }`}
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                E-posta
              </label>
              <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors ${
                isDark
                  ? 'bg-dark-bg border-dark-border focus-within:border-primary-500'
                  : 'bg-light-bg border-light-border focus-within:border-primary-500'
              }`}>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-mail-line ${isDark ? 'text-dark-muted' : 'text-light-muted'}`} />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ornek@email.com"
                  className={`flex-1 bg-transparent text-sm outline-none ${
                    isDark ? 'text-dark-text placeholder:text-dark-muted' : 'text-light-text placeholder:text-light-muted'
                  }`}
                  required
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                Telefon (isteğe bağlı)
              </label>
              <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors ${
                isDark
                  ? 'bg-dark-bg border-dark-border focus-within:border-primary-500'
                  : 'bg-light-bg border-light-border focus-within:border-primary-500'
              }`}>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-phone-line ${isDark ? 'text-dark-muted' : 'text-light-muted'}`} />
                </div>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="05XX XXX XX XX"
                  className={`flex-1 bg-transparent text-sm outline-none ${
                    isDark ? 'text-dark-text placeholder:text-dark-muted' : 'text-light-text placeholder:text-light-muted'
                  }`}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                Şifre
              </label>
              <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors ${
                isDark
                  ? 'bg-dark-bg border-dark-border focus-within:border-primary-500'
                  : 'bg-light-bg border-light-border focus-within:border-primary-500'
              }`}>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-lock-line ${isDark ? 'text-dark-muted' : 'text-light-muted'}`} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="En az 6 karakter"
                  className={`flex-1 bg-transparent text-sm outline-none ${
                    isDark ? 'text-dark-text placeholder:text-dark-muted' : 'text-light-text placeholder:text-light-muted'
                  }`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`w-5 h-5 flex items-center justify-center ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                Şifre Tekrar
              </label>
              <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors ${
                isDark
                  ? 'bg-dark-bg border-dark-border focus-within:border-primary-500'
                  : 'bg-light-bg border-light-border focus-within:border-primary-500'
              }`}>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-lock-password-line ${isDark ? 'text-dark-muted' : 'text-light-muted'}`} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Şifrenizi tekrar girin"
                  className={`flex-1 bg-transparent text-sm outline-none ${
                    isDark ? 'text-dark-text placeholder:text-dark-muted' : 'text-light-text placeholder:text-light-muted'
                  }`}
                  required
                />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading || success}
              className="w-full py-2.5 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-user-add-line" />
                  </div>
                  Hesap Oluştur
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className={`text-center text-xs mt-6 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
          Zaten hesabınız var mı?{' '}
          <Link to="/login" className="text-primary-500 hover:text-primary-600 font-medium">
            Giriş Yap
          </Link>
        </p>
      </div>
    </div>
  );
}